import { z } from 'zod';

import { defineNode, total } from '../define';
import { isTimeZone } from '../zoned';

/** 24-hour "HH:MM". */
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
/** Days for Daily timing, Monday first. */
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const schedulePublish = defineNode({
  type: 'output.schedulePublish',
  version: 1,
  category: 'output',
  label: 'Schedule & publish',
  description: 'Post to one or many accounts; crosspost with smart time slots.',
  icon: 'PaperPlaneTilt',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['reel', 'carousel'] }],
  // Published posts, for a Delay → Pick winner experiment to measure.
  outputs: [{ id: 'published', label: 'Published', type: 'same' }],
  mode: 'map',
  executor: 'publish',
  config: z
    .object({
      postTo: z
        .enum(['pickedAccounts', 'personaAccounts'])
        .default('pickedAccounts')
        .meta({
          title: 'Post to',
          description:
            'Persona accounts: each post goes to the accounts of the persona in it',
          widget: 'segmented',
        }),
      accountIds: z
        .array(z.uuid())
        .default([])
        .meta({
          title: 'Accounts',
          widget: 'accounts',
          when: { postTo: ['pickedAccounts'] },
        }),
      personaIds: z
        .array(z.uuid())
        .default([])
        .meta({
          title: 'Personas',
          description:
            'Posts without a persona are shared out between these in turn. None ticked: every persona with accounts',
          widget: 'personas',
          when: { postTo: ['personaAccounts'] },
        }),
      crosspost: z.boolean().default(false).meta({
        title: 'Crosspost each reel',
        description:
          'Same post to every account (or every account of its persona); captions adapt per platform',
      }),
      timing: z.enum(['bestTime', 'spaced', 'fixed', 'daily']).default('bestTime').meta({
        title: 'Timing',
        widget: 'segmented',
      }),
      spacingHours: z
        .int()
        .min(1)
        .max(72)
        .default(3)
        .meta({ title: 'At least (h) apart', when: { timing: ['spaced', 'fixed'] } }),
      fixedAt: z.iso
        .datetime({ offset: true })
        .optional()
        .meta({ title: 'At', widget: 'datetime', when: { timing: ['fixed'] } }),
      // Daily: set times on set days, e.g. 09:00 and 18:00 every day, from a date.
      dailyTimes: z
        .array(z.string().regex(TIME, 'Use 24-hour times like 09:00'))
        .max(12)
        .default(['09:00', '18:00'])
        .meta({ title: 'Times each day', widget: 'times', when: { timing: ['daily'] } }),
      weekdays: z
        .array(z.enum(WEEKDAYS))
        .default([...WEEKDAYS])
        .meta({ title: 'On', widget: 'chips', when: { timing: ['daily'] } }),
      startOn: z.iso
        .date()
        .optional()
        .meta({
          title: 'Starting',
          description: 'Leave empty to start at the next free time',
          widget: 'date',
          when: { timing: ['daily'] },
        }),
      timeZone: z
        .string()
        .max(64)
        .refine(isTimeZone, 'Pick a time zone from the list')
        .optional()
        .meta({
          title: 'Time zone',
          description:
            'Leave empty to use each persona’s time zone, else the workspace’s',
          widget: 'timeZone',
          when: { timing: ['daily'] },
        }),
    })
    .refine(
      (config) => config.postTo !== 'pickedAccounts' || config.accountIds.length > 0,
      {
        error: 'Pick at least one account',
        path: ['accountIds'],
      },
    )
    .refine((config) => config.timing !== 'fixed' || config.fixedAt !== undefined, {
      error: 'Pick a date and time',
      path: ['fixedAt'],
    })
    .refine((config) => config.timing !== 'daily' || config.dailyTimes.length > 0, {
      error: 'Add a time',
      path: ['dailyTimes'],
    })
    .refine((config) => config.timing !== 'daily' || config.weekdays.length > 0, {
      error: 'Pick at least one day',
      path: ['weekdays'],
    }),
  cardinality: (_config, inputs) => ({ published: total(inputs) }),
});

export const saveToLibrary = defineNode({
  type: 'output.saveToLibrary',
  version: 1,
  category: 'output',
  label: 'Save to library',
  description: 'Keep outputs in a collection for later.',
  icon: 'BookmarkSimple',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [],
  mode: 'map',
  executor: 'control',
  config: z.object({
    collectionId: z.uuid().optional().meta({ title: 'Collection', widget: 'collection' }),
    personaId: z.uuid().optional().meta({
      title: 'Persona',
      description: 'Tags saved items with this persona, unless they already show one',
      widget: 'persona',
    }),
    tags: z
      .array(z.string().trim().min(1).max(40))
      .max(10)
      .default([])
      .meta({ title: 'Tags', widget: 'tags' }),
    carouselParts: z.enum(['whole', 'firstSlide']).default('whole').meta({
      title: 'Carousels',
      description: 'First slide: keeps just the hook slide, as an image',
      widget: 'segmented',
    }),
  }),
  cardinality: () => ({}),
});

export const notify = defineNode({
  type: 'output.notify',
  version: 1,
  category: 'output',
  label: 'Notify',
  description: 'Ping Slack or email when a run finishes or needs review.',
  icon: 'Bell',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [],
  mode: 'collect',
  executor: 'control',
  config: z.object({
    channel: z
      .enum(['email', 'slack'])
      .default('email')
      .meta({ title: 'Send by', widget: 'segmented' }),
  }),
  cardinality: () => ({}),
});

export const exportFiles = defineNode({
  type: 'output.export',
  version: 1,
  category: 'output',
  label: 'Export',
  description: 'Download files or send them to a webhook.',
  icon: 'Export',
  status: 'ga',
  inputs: [{ id: 'in', accepts: 'any' }],
  outputs: [],
  mode: 'map',
  executor: 'control',
  config: z
    .object({
      destination: z.enum(['download', 'webhook']).default('download').meta({
        title: 'Send to',
        widget: 'segmented',
      }),
      webhookUrl: z
        .url({ protocol: /^https$/, error: 'Use an https:// address' })
        .optional()
        .meta({
          title: 'Webhook URL',
        }),
    })
    .refine(
      (config) => config.destination !== 'webhook' || config.webhookUrl !== undefined,
      {
        error: 'Add the webhook URL',
        path: ['webhookUrl'],
      },
    ),
  cardinality: () => ({}),
});

export const OUTPUTS = [schedulePublish, saveToLibrary, notify, exportFiles];
